import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Download, Upload, FileText, FileJson, FileType2, Lock } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  exportLettersAsPdf,
  exportLettersAsTxt,
  exportLettersAsMarkdown,
  exportFullBackup,
  importBackup,
} from "@/lib/backup";

export default function Backup() {
  const fileInput = useRef(null);
  const [restoring, setRestoring] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (exportFile, label) => {
    if (exporting) return;
    setExporting(true);
    try {
      const result = await exportFile();
      if (result?.cancelled) return;
      if (result?.saved) {
        toast.success(`${label} saved.`, { description: "Keep the file somewhere private." });
      } else {
        toast(`${label} download started.`, { description: "Check that the file downloaded. Keep it somewhere safe." });
      }
    } catch (_e) {
      toast("Couldn’t export that just now.", {
        description: "Your writing is still on this device. Try again before switching devices.",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleRestore = async (file) => {
    if (!file) return;
    setRestoring(true);
    try {
      const text = await file.text();
      const summary = await importBackup(text);
      const parts = [];
      if (summary.letters) parts.push(`${summary.letters} letter${summary.letters === 1 ? "" : "s"}`);
      if (summary.mosaic) parts.push(`${summary.mosaic} mosaic piece${summary.mosaic === 1 ? "" : "s"}`);
      if (summary.drafts) parts.push(`${summary.drafts} draft${summary.drafts === 1 ? "" : "s"}`);
      if (summary.journal) parts.push(`${summary.journal} journal entr${summary.journal === 1 ? "y" : "ies"}`);
      toast.success("Restored.", {
        description: parts.length ? parts.join(" · ") : "Nothing new to add.",
      });
    } catch (e) {
      toast(e.message || "That file couldn't be read.", {
        description: "Restore did not finish. Some items may already have been restored.",
      });
    } finally {
      setRestoring(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <PageContainer testid="backup-page">
      <PageHeader
        title="Backup & export"
        subtitle="Take your writing with you. Nothing leaves the device unless you save it."
        variant="exit"
        exitTo="/settings"
        exitLabel="Settings"
        testid="backup-exit"
      />

      {/* Privacy strip */}
      <div
        className="rounded-2xl p-3 mb-6 flex items-center gap-2.5"
        style={{ background: "rgba(91,57,84,0.05)" }}
      >
        <Lock size={14} style={{ color: "var(--un-plum)" }} />
        <p
          className="text-[12px] leading-snug"
          style={{ color: "var(--un-text-2)" }}
        >
          Files are saved <strong>directly to your device</strong>. Backup and export do not require an account.
          Exported files are readable and are not encrypted. Keep them somewhere private.
        </p>
      </div>

      {/* Export letters */}
      <section className="mb-7">
        <h2
          className="text-[18px] font-medium mb-2"
          style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
        >
          Export your letters
        </h2>
        <p
          className="text-[13px] mb-4 leading-relaxed"
          style={{ color: "var(--un-text-3)" }}
        >
          A readable copy of every tomorrow-you letter you've written, in the
          format you prefer.
        </p>
        <div className="flex flex-col gap-2">
          <SoftButton
            variant="secondary"
            size="md"
            iconLeft={<FileText size={16} />}
            onClick={() => handleExport(exportLettersAsPdf, "Letters")}
            disabled={exporting}
            data-testid="backup-export-pdf"
          >
            Download as PDF
          </SoftButton>
          <SoftButton
            variant="secondary"
            size="md"
            iconLeft={<FileType2 size={16} />}
            onClick={() => handleExport(exportLettersAsTxt, "Letters")}
            disabled={exporting}
            data-testid="backup-export-txt"
          >
            Download as plain text
          </SoftButton>
          <SoftButton
            variant="secondary"
            size="md"
            iconLeft={<FileType2 size={16} />}
            onClick={() => handleExport(exportLettersAsMarkdown, "Letters")}
            disabled={exporting}
            data-testid="backup-export-md"
          >
            Download as Markdown
          </SoftButton>
        </div>
      </section>

      {/* Full backup */}
      <section className="mb-7">
        <h2
          className="text-[18px] font-medium mb-2"
          style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
        >
          Full backup
        </h2>
        <p
          className="text-[13px] mb-4 leading-relaxed"
          style={{ color: "var(--un-text-3)" }}
        >
          Letters, mosaic pieces, drafts, and journal entries — saved as a
          single file you keep. Use this if you're switching devices or just
          want a copy of your own.
        </p>
        <SoftButton
          variant="primary"
          size="md"
          iconLeft={<Download size={16} />}
          onClick={() => handleExport(exportFullBackup, "Backup")}
          disabled={exporting}
          data-testid="backup-export-full"
        >
          Download full backup
        </SoftButton>
      </section>

      {/* Restore */}
      <section className="mb-10">
        <h2
          className="text-[18px] font-medium mb-2"
          style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
        >
          Restore from a backup
        </h2>
        <p
          className="text-[13px] mb-4 leading-relaxed"
          style={{ color: "var(--un-text-3)" }}
        >
          Add the contents of a previous backup back into this device. Existing
          letters and mosaic pieces stay — restored items are added alongside.
        </p>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          onChange={(e) => handleRestore(e.target.files?.[0])}
          className="hidden"
          data-testid="backup-restore-input"
        />
        <SoftButton
          variant="secondary"
          size="md"
          iconLeft={<FileJson size={16} />}
          onClick={() => fileInput.current?.click()}
          disabled={restoring}
          data-testid="backup-restore-btn"
        >
          {restoring ? "Restoring…" : "Choose a backup file"}
        </SoftButton>
      </section>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="text-[11px] leading-relaxed text-center mb-10"
        style={{ color: "var(--un-text-3)" }}
      >
        Backup and export do not require an account. Exported files are readable and are not encrypted. Keep them somewhere private.
      </motion.p>
    </PageContainer>
  );
}
